import { get, post, put } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SyncScheduleInfo, SyncStateInfo } from '@uncain/shared';
import { useRef } from 'react';

/**
 * Poll sync state. When status is 'running', polls every 3s.
 * On transition from 'running' to 'completed', invalidates posture queries.
 */
export function useSyncState() {
	const queryClient = useQueryClient();
	const prevStatusRef = useRef<string | null>(null);

	return useQuery<SyncStateInfo>({
		queryKey: queryKeys.sync.state(),
		queryFn: async () => {
			const state = await get<SyncStateInfo>('/sync');

			// Detect running → completed transition and invalidate posture queries
			if (prevStatusRef.current === 'running' && state.status !== 'running') {
				queryClient.invalidateQueries({ queryKey: queryKeys.posture.all });
			}
			prevStatusRef.current = state.status;

			return state;
		},
		refetchInterval: (query) => {
			const data = query.state.data;
			return data?.status === 'running' ? 3000 : false;
		},
	});
}

/**
 * Trigger an ad-hoc sync (POST /sync).
 */
export function useSyncTrigger() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => post<{ workflowInstanceId: string; status: string }>('/sync'),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.sync.state() });
		},
	});
}

/**
 * Update periodic sync schedule (PUT /sync/schedule).
 */
export function useUpdateSyncSchedule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { enabled: boolean; intervalSeconds?: number }) =>
			put<SyncScheduleInfo>('/sync/schedule', params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.sync.state() });
		},
	});
}
